<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col text-center">
        <h2>Procès verbal de l’assemblée générale</h2>
        <h3>
          {{ $moment(toLocalTime(meeting.date)).locale("fr").format("[du] DD MMMM YYYY") }}
        </h3>
      </div>
    </div>
    <div class="row my-4">
      <div class="col text-center font-italic small">
        <h3>{{ meetingDetails.BuildingData.name }}</h3>
        <div v-if="meetingDetails.BuildingData.address" class="text-br-line">
          {{ meetingDetails.BuildingData.address }}
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col">
        Chers membres du syndicat des copropriétaires,
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        Les copropriétaires se sont réunis en assemblée générale, laquelle s’est tenue
        {{ $moment(toLocalTime(meeting.date)).locale("fr").format("[le] DD MMMM YYYY")
        }}<span
          v-if="
            meeting.allowed_modalities.correspondence &&
            !meeting.allowed_modalities.physical &&
            !meeting.allowed_modalities.video
          "
          >, exclusivement par vote par correspondance</span
        ><span v-else-if="meeting.allowed_modalities.video || meeting.allowed_modalities.physical"
          >, à partir de
          {{ $moment(toLocalTime(meeting.date)).locale("fr").format("HH[h]mm") }}</span
        ><span v-if="meeting.allowed_modalities.physical">
          à l'adresse :
          <!-- eslint-disable-next-line prettier/prettier -->
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="meeting.address"
            class="text-br-line font-weight-bold mb-3"
            v-html="meeting.address"
          ></div>
          <div v-else class="text-danger">pas d'adresse définie</div>
        </span>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <div v-if="meeting.access" class="mt-3 text-center">
          Modalités d'accès :
          <div class="text-br-line font-weight-bold" v-html="meeting.access"></div>
        </div>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <div v-if="meeting.allowed_modalities.video" class="mt-3">
          Pour exprimer le vote et télécharger les documents, les copropriétaires peuvent accéder au
          lien :
          <a class="text-dark" href="https://participant.domeo-ag.com">
            https://participant.domeo-ag.com
          </a>
        </div>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        Il a été dressé une feuille de présence qui a été signée par tous les copropriétaires
        présents et par les mandataires de ceux qui étaient représentés.
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        Après vérification de la feuille de présence, il est constaté que :
      </div>
    </div>

    <appPresence :first-resolution-vote="firstResolutionVote" />

    <appArticle22
      v-if="
        generalDetails &&
        generalDetails.article22IsActive &&
        generalDetails.buildingOwnersWithReducedTantiemes &&
        generalDetails.buildingOwnersWithReducedTantiemes.length > 0
      "
      :general-details="generalDetails"
    />

    <appPlafonMajority
      v-if="
        generalDetails &&
        generalDetails.plafonMajorityIsActive &&
        generalDetails.buildingOwnersWithReducedTantiemes &&
        generalDetails.buildingOwnersWithReducedTantiemes.length > 0
      "
      :general-details="generalDetails"
    />

    <div class="row mt-4 mb-2">
      <div class="col">Afin de délibérer sur l’ordre du jour suivant :</div>
    </div>

    <div
      v-for="(event, voteOrder) in resolutionEvents"
      :key="`summary-${++voteOrder}`"
      class="row flex-nowrap"
    >
      <appResolutionVoteSummary
        v-if="event.event == meetingMinutesNames.resolution_vote"
        :event="event"
        :vote-order="incrementResolutionVoteOrderSummary()"
        :resolution="resolutions.find(r => r.id === event.resolution_id)"
        :parent-resolution="resolutions.find(r => r.id === event.parent_resolution_id)"
      />
    </div>

    <div class="new-page"></div>

    <div
      v-for="(event, resolutionOrder) in resolutionEvents"
      :key="`details-${++resolutionOrder}`"
      class="row no-break my-4"
    >
      <appResolutionVoteDetail
        v-if="event.event == meetingMinutesNames.resolution_vote"
        :event="event"
        :events-major-changes="resolutionMajorChangeEvents"
        :vote-order="
          updateCurrentResolutionVoteNumber(resolutions.find(r => r.id === event.resolution_id))
        "
        :resolution="resolutions.find(r => r.id === event.resolution_id)"
        :parent-resolution="resolutions.find(r => r.id === event.parent_resolution_id)"
        :is-revote-event-resolution="isRevoteEventResolution(event, event.resolution_id)"
      />

      <appParticipantArrived
        v-if="event.event == meetingMinutesNames.participant_arrived"
        :vote-order="lastResolutionVoteNumber"
        :event="event"
      />

      <appContactArrived
        v-if="event.event == meetingMinutesNames.contact_arrived"
        :vote-order="lastResolutionVoteNumber"
        :event="event"
      />

      <appMandateAdded v-if="event.event == meetingMinutesNames.mandate_added" :event="event" />

      <appMandateMoved v-if="event.event == meetingMinutesNames.mandate_moved" :event="event" />

      <appMandateLeft v-if="event.event == meetingMinutesNames.mandate_left" :event="event" />

      <appContactLeft
        v-if="event.event == meetingMinutesNames.contact_left"
        :vote-order="lastResolutionVoteNumber"
        :event="event"
      />

      <appParticipantLeft
        v-if="event.event == meetingMinutesNames.participant_left"
        :vote-order="lastResolutionVoteNumber"
        :event="event"
      />
    </div>
    <hr class="my-5" />
    <div
      v-if="
        meeting.allowed_modalities.correspondence &&
        !meeting.allowed_modalities.physical &&
        !meeting.allowed_modalities.video
      "
      class="row"
    >
      <div class="col">
        L'ordre du jour étant épuisé et l'ensemble des votes par correspondances étant enregistrés,
        il est procédé à l'édition du procès-verbal.
      </div>
    </div>
    <div v-if="meeting.allowed_modalities.physical || meeting.allowed_modalities.video" class="row">
      <div class="col">
        L’ordre du jour étant épuisé et plus aucune question n’étant posée, l’assemblée se termine à
        {{ $moment(toLocalTime(getMeetingEndEvent().date)).locale("fr").format("HH[h]mm") }}.
      </div>
    </div>
    <div
      v-if="
        !meeting.allowed_modalities.correspondence &&
        meeting.allowed_modalities.physical &&
        meeting.allowed_modalities.video
      "
      class="row"
    >
      <div class="col">
        L’ordre du jour étant épuisé et plus aucune question n’étant posée, l’assemblée se termine à
        {{ $moment(toLocalTime(getMeetingEndEvent().date)).locale("fr").format("HH[h]mm") }}.
      </div>
    </div>

    <div class="row mt-4">
      <!-- eslint-disable vue/no-v-html -->
      <div class="col" v-html="meetingDetails.BuildingData.legal"></div>
    </div>

    <div class="no-break">
      <div class="row mt-4">
        <div class="col-4 offset-5">
          Fait à :
        </div>
        <div
          v-if="meeting.allowed_modalities.physical"
          class="col-3"
          v-html="meeting.address"
        ></div>
      </div>

      <div class="row mt-4">
        <div class="col-4">
          Le Président de séance
        </div>
        <div class="col-4">
          Le Secrétaire de séance
        </div>
        <div class="col-4">
          Le(s) Scrutateur(s)
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import presence from "./components/presence";
import article22 from "./components/article22";
import plafonMajority from "./components/plafonMajority";
import resolutionVoteDetail from "./components/resolutionVoteDetail";
import resolutionVoteSummary from "./components/resolutionVoteSummary";
import participantArrived from "./components/participantArrived";
import contactArrived from "./components/contactArrived";
import mandateAdded from "./components/mandateAdded";
import mandateMoved from "./components/mandateMoved";
import mandateLeft from "./components/mandateLeft";
import contactLeft from "./components/contactLeft";
import participantLeft from "./components/participantLeft";

import { meetingMinutesNames } from "@/constants/meeting.js";

export default {
  provide() {
    return {
      meeting: this.meeting,
      generatePvOption: this.generatePvOption,
    };
  },
  components: {
    appPresence: presence,
    appArticle22: article22,
    appPlafonMajority: plafonMajority,
    appResolutionVoteDetail: resolutionVoteDetail,
    appResolutionVoteSummary: resolutionVoteSummary,
    appParticipantArrived: participantArrived,
    appContactArrived: contactArrived,
    appMandateAdded: mandateAdded,
    appMandateMoved: mandateMoved,
    appMandateLeft: mandateLeft,
    appContactLeft: contactLeft,
    appParticipantLeft: participantLeft,
  },
  props: ["meeting", "resolutions", "generatePvOption"],
  data() {
    return {
      meetingMinutesNames,
      firstResolutionVote: undefined,
      generalDetails: undefined,
      resolutionEvents: undefined,
      resolutionMajorChangeEvents: undefined,
      resolutionsParent: [],
      resolutionNr: 0,
      showedResolutions: [],
      lastResolutionVoteNumber: 1,
      lastIndexResolutionVote: 1,
      lastIndexResolutionVoteSummary: 1,
    };
  },
  created() {
    this.toUtc();
    const meetingEnd = this.meeting.minutes.find(
      event => event.event === this.meetingMinutesNames.meeting_end
    );
    this.generalDetails = this.meeting.minutes.find(
      event => event.event === this.meetingMinutesNames.general_details
    );

    this.firstResolutionVote = this.getFirstResolutionVote();

    this.resolutionEvents = this.meeting.minutes.filter(
      event =>
        event.event === this.meetingMinutesNames.resolution_vote ||
        (event.event === this.meetingMinutesNames.participant_arrived &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.contact_arrived &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.mandate_added &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.mandate_moved &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.mandate_left &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.contact_left &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date) ||
        (event.event === this.meetingMinutesNames.participant_left &&
          event.date > this.firstResolutionVote.date &&
          event.date < meetingEnd.date)
    );
    this.resolutionEvents = this.resolutionEvents.sort(function (a, b) {
      var dateA = new Date(a.date),
        dateB = new Date(b.date);
      return dateA - dateB;
    });
    this.resolutionMajorChangeEvents = this.meeting.minutes.filter(
      event => event.event === this.meetingMinutesNames.resolution_major_change
    );
  },
  methods: {
    updateCurrentResolutionVoteNumber(resolution) {
      // NOTE: Set last resolution vote number to be used for participants presence logs
      let parentResolution = null;
      if (resolution.parent_resolution_id) {
        parentResolution = this.resolutions.find(r => r.id === resolution.parent_resolution_id);
      }
      this.lastResolutionVoteNumber = parentResolution
        ? parseFloat(parentResolution.resolution_number + "." + resolution.resolution_number)
        : Number(resolution.resolution_number);
      // NOTE: Return counter value not the resolution number value
      return this.incrementResolutionVoteOrder();
    },
    incrementResolutionVoteOrder() {
      return this.lastIndexResolutionVote++;
    },
    incrementResolutionVoteOrderSummary() {
      return this.lastIndexResolutionVoteSummary++;
    },
    getMeetingEndEvent() {
      return this.meeting.minutes.find(
        event => event.event === this.meetingMinutesNames.meeting_end
      );
    },
    toUtc() {
      this.meeting.minutes.forEach(event => {
        if (event.date) {
          event.date = this.$moment
            .parseZone(event.date)
            .utcOffset(0, true)
            .format("YYYY-MM-DDTHH:mm:ssZ");
        }
      });
    },
    getFirstResolutionVote() {
      const resolutionVoteEvents = this.meeting.minutes.filter(
        event => event.event === this.meetingMinutesNames.resolution_vote
      );
      const filteredResolutionVoteEvents = resolutionVoteEvents.sort(function (a, b) {
        let dateA = new Date(a.date);
        let dateB = new Date(b.date);
        return dateA - dateB;
      });
      return filteredResolutionVoteEvents[0];
    },
    // NOTE: Find if resolution_id has another event with revote_majority_id
    isRevoteEventResolution(event, resolutionId) {
      if (event.revote_majority_id) {
        return false;
      } else {
        const eventWithRevoteMajority = this.resolutionEvents.find(
          item => item.resolution_id == resolutionId && item.revote_majority_id
        );
        return eventWithRevoteMajority ? true : false;
      }
    },
  },
};
</script>
